import React, { useState, useEffect } from 'react';
// import axios from 'axios';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { CodeSnippet } from './CodeSnippet';
import { Response } from './Response';
import { ParameterTable } from './ParameterTable';
import { BodyV2 } from './BodyV2';

const ResponseHeading = styled('h4', {
  marginBottom: '$sm',
  fontSize: '18px !important',
  fontWeight: 400,
});

const SandBoxContainer = styled('div', {
  position: 'relative',
  backgroundColor: '$white',
  [ResponseHeading]: {
    fontSize: '$xl',
  },
});

const ActionContainer = styled('div', {
  zIndex: 2,
  '@screen >= $xl': {
    position: 'absolute',
    right: 0,
    marginTop: 8,
  },
});

const CallContainer = styled('div', {
  display: 'flex',
  position: 'relative',
  zIndex: 2,
});

const CallType = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid $gray6',
  backgroundColor: '$white',
  borderRadius: 4,
  padding: '0 $sm',
  fontWeight: '$bold',
  height: 38,
  minWidth: 80,
  textTransform: 'uppercase',
  variants: {
    variant: {
      get: {
        color: '$success1',
        borderColor: '$success1',
        backgroundColor: '$success2',
      },
      post: {
        color: '$info1',
        borderColor: '$info1',
        backgroundColor: '$info2',
      },
      put: {
        color: '$warning1',
        borderColor: '$warning1',
        backgroundColor: '$warning2',
      },
      delete: {
        color: '$error1',
        borderColor: '$error1',
        backgroundColor: '$error2',
      },
    },
  },
});

const UrlInputContainer = styled('div', {
  flexGrow: 1,
  margin: '0 $sm',
});

export const ServiceSandboxV2 = ({ swagger, currentApi }) => {
  const { method, path } = currentApi;
  const { servers } = swagger;
  const [queryParams, setQueryParams] = useState([]);
  const [headerParams, setHeaderParams] = useState([]);
  const [pathParams, setPathParams] = useState([]);
  const [formDataParams, setFormDataParams] = useState([]);
  const [responseOptions, setResponseOptions] = useState([]);
  const [requestMap, setRequestMap] = useState({});
  const [serviceResponse, setServiceResponse] = useState(null);
  const [selectedExample, setSelectedExample] = useState(null);
  const [isCodeOpen, openCodeModal] = useState(false);

  const getPathUrl = () => {
    if (!servers || !servers[0] || !path) return '';
    return `${servers[0].url}${path}`;
  };

  const url = getPathUrl();

  const form = useForm({
    defaultValues: { url, response: responseOptions[0] },
  });

  const getQueryAndHeaderParams = () => {
    if (!currentApi || !currentApi.parameters) return;
    const query = [];
    const header = [];
    const path = [];
    const formData = [];
    currentApi.parameters.forEach((param) => {
      const data = {
        key: param.name,
        value: param.schema.example,
        type: param.schema ? param.schema.type : 'string',
        format: param.schema ? param.schema.format : '',
        description: param.description || '',
        required: param.required || false,
      };
      if (param.in === 'query') {
        query.push(data);
      } else if (param.in === 'header') {
        header.push(data);
      } else if (param.in === 'path') {
        path.push(data);
      } else if (param.in === 'formData') {
        formData.push(data);
      }
    });

    const envHeader = {
      key: 'env',
      value: 'sandbox',
      type: 'string',
      description: 'env',
      required: false,
    };

    header.push(envHeader);

    setQueryParams(query);
    setHeaderParams(header);
    setPathParams(path);
    setFormDataParams(formData);
  };

  useEffect(() => {
    if (!requestMap) return;

    const selectedValue = form.getValues('response');
    const contentType = requestMap[selectedValue]?.type;
    const schema = requestMap[selectedValue]?.schema;

    const exampleValues = requestMap[selectedValue]?.value || {};

    if (contentType) {
      const contentTypeHeaderIndex = headerParams.findIndex((header) => {
        return header.key.toLowerCase() === 'content-type';
      });

      const updatedHeaders = [...headerParams];
      const contentTypeHeader = {
        key: 'Content-Type',
        value: contentType,
        type: 'string',
        description: 'The media type of the request body',
        required: true,
      };

      if (contentTypeHeaderIndex >= 0) {
        updatedHeaders[contentTypeHeaderIndex] = contentTypeHeader;
      } else {
        updatedHeaders.push(contentTypeHeader);
      }

      setHeaderParams(updatedHeaders);

      if (contentType.includes('multipart/form-data')) {
        const formFields = [];

        if (schema && schema.properties) {
          Object.entries(schema.properties).forEach(
            ([propName, propSchema]) => {
              const exampleValue = exampleValues[propName];

              let valueToUse = '';
              let typeToUse = propSchema.type || 'string';

              if (exampleValue !== undefined) {
                if (typeof exampleValue === 'object') {
                  valueToUse = JSON.stringify(exampleValue);
                  typeToUse = 'object';
                } else {
                  valueToUse = exampleValue;
                }
              } else if (propSchema.example !== undefined) {
                valueToUse =
                  propSchema.type === 'object'
                    ? JSON.stringify(propSchema.example)
                    : propSchema.example;
              }

              typeToUse = propSchema.format === 'binary' ? 'file' : typeToUse;

              formFields.push({
                key: propName,
                format: propSchema.format || '',
                value: valueToUse,
                type: typeToUse,
                in: 'formData',
                description:
                  propSchema.description || `Form field: ${propName}`,
                required: schema.required?.includes(propName) || false,
              });
            }
          );
        } else if (Object.keys(exampleValues).length > 0) {
          Object.entries(exampleValues).forEach(([key, value]) => {
            formFields.push({
              key,
              format: '',
              value: typeof value === 'object' ? JSON.stringify(value) : value,
              type: typeof value === 'object' ? 'object' : typeof value,
              description: `Form field: ${key}`,
              required: false,
            });
          });
        }

        setSelectedExample(null);
        setFormDataParams(formFields);
      } else if (contentType.includes('application/json')) {
        if (formDataParams.length > 0) {
          const jsonBody = formDataParams.reduce((obj, param) => {
            let value = param.value;
            if (param.type === 'object' && typeof value === 'string') {
              try {
                value = JSON.parse(value);
              } catch {
                // Keep as string if parsing fails
              }
            }
            obj[param.key] = value;
            return obj;
          }, {});

          setFormDataParams([]);
          setSelectedExample(jsonBody);
        } else {
          setSelectedExample(exampleValues);
        }
      }
    }
  }, [requestMap, form.getValues('response')]);

  useEffect(() => {
    setServiceResponse();
  }, [currentApi]);

  const getAllSampleRequests = () => {
    const { requestBody } = currentApi || {};
    if (!requestBody || !requestBody.content) return [];

    const reqMap = {};
    const responseOpts = [];

    Object.keys(requestBody.content).forEach((contentType) => {
      const content = requestBody.content[contentType];
      const schema = content?.schema;
      const examplesList = content?.examples || {};

      if (Object.keys(examplesList).length === 0 && schema) {
        const defaultExample = {
          value: schema.example || {},
          summary: 'Default Example',
        };

        const requestData = {
          type: contentType,
          value: defaultExample.value,
          schema: schema,
        };

        responseOpts.push({
          label: defaultExample.summary,
          value: defaultExample.summary,
        });
        reqMap[defaultExample.summary] = requestData;
      } else {
        Object.keys(examplesList).forEach((key) => {
          const example = examplesList[key];
          const requestData = {
            type: contentType,
            value: example.value || {},
            schema: schema,
          };
          responseOpts.push({ label: key, value: key });
          reqMap[key] = requestData;
        });
      }
    });

    setResponseOptions(responseOpts);
    setRequestMap(reqMap);
  };

  useEffect(() => {
    if (form.getValues('url') !== url) {
      form.setValue('url', url);
    }

    getQueryAndHeaderParams();
    getAllSampleRequests();
  }, [url]);

  useEffect(() => {
    if (responseOptions.length > 0) {
      const defaultOption = responseOptions[0].value;
      form.setValue('response', defaultOption);

      if (requestMap && requestMap[defaultOption]) {
        setSelectedExample(requestMap[defaultOption].value);
      }
    }
  }, [responseOptions, requestMap]);

  const getCurrentParameters = () => {
    const currentQueryParams = queryParams.map((param) => {
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    const currentHeaderParams = headerParams.map((param) => {
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    const currentPathParams = pathParams.map((param) => {
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    const currentFormDataParams = formDataParams.map((param) => {
      if (param.type === 'file') {
        return {
          ...param,
          value: form.getValues(`${param.key}_file`) || param.value,
        };
      }
      return {
        ...param,
        value: form.getValues(param.key) || param.value,
      };
    });

    return {
      queryParams: currentQueryParams,
      headerParams: currentHeaderParams,
      pathParams: currentPathParams,
      formDataParams: currentFormDataParams,
    };
  };

  const handleSendRequest = () => {
    const selectedResponseValue = form.getValues('response');

    if (currentApi && currentApi.responses) {
      let responseData = null;
      Object.entries(currentApi.responses).forEach(
        ([statusCode, responseObj]) => {
          if (responseObj.status === '200') {
            responseData = responseObj;
          }
        }
      );

      if (responseData) {
        const { content } = responseData;

        const respContentTypes = content ? Object.keys(content) : [];

        if (respContentTypes.length === 0) {
          const defaultResponse = {
            data: 'No sample responses are defined for this API',
          };

          setServiceResponse(defaultResponse);
          return;
        }

        const allSampleResponses = {};

        respContentTypes.forEach((type) => {
          if (content[type].example) {
            allSampleResponses[type] = {
              value: content[type].example,
            };
          } else if (content[type].examples) {
            const examples = content[type].examples;
            Object.keys(examples).forEach((key) => {
              allSampleResponses[key] = {
                value: examples[key].value,
              };
            });
          } else if (content[type].schema?.example) {
            allSampleResponses[type] = {
              value: content[type].schema.example,
            };
          }
        });

        const sampleResponse =
          Object.keys(allSampleResponses).length > 0
            ? allSampleResponses[selectedResponseValue]?.value
            : 'No sample response found for the selected option';

        const respData = {
          data:
            sampleResponse ||
            'No sample response found for the selected option',
        };

        setServiceResponse(respData);
      } else {
        const defaultResponse = {
          data: 'No sample response found for the selected option',
        };

        setServiceResponse(defaultResponse);
      }
    } else {
      const errorResponse = {
        data: 'No sample responses are defined for this API',
      };

      setServiceResponse(errorResponse);
    }
  };

  return (
    <React.Fragment>
      <SandBoxContainer>
        <FormProvider state={form}>
          <CallContainer>
            <CallType variant={method.toLowerCase()} css={{ fontSize: '15px' }}>
              {method}
            </CallType>
            <UrlInputContainer>
              <TextInput
                label="API URL"
                hideLabel
                width="auto"
                isReadOnly
                model="url"
              ></TextInput>
            </UrlInputContainer>
            <Button
              type="submit"
              fontSize="15px"
              css={{ width: 150, borderRadius: 4 }}
              size="38px"
              onClick={handleSendRequest}
            >
              Send Request
            </Button>
          </CallContainer>
          <ActionContainer>
            <Layout.Group alignItems="bottom">
              <SelectInput
                label={
                  <span style={{ fontSize: '15px' }}>Sample Responses</span>
                }
                placeholder="Response"
                onChange={(e) => {
                  setServiceResponse(null);

                  const selectedValue = e;
                  const requestData = requestMap[selectedValue];

                  if (requestData) {
                    const contentType = requestData.type;
                    const newExampleValue = requestData.value;

                    if (contentType.includes('multipart/form-data')) {
                      setSelectedExample(null);

                      if (requestData.schema && requestData.schema.properties) {
                        const formFields = Object.entries(
                          requestData.schema.properties
                        ).map(([propName, propSchema]) => {
                          const exampleValue = newExampleValue[propName];

                          let valueToUse = '';
                          let typeToUse = propSchema.type || 'string';

                          if (exampleValue !== undefined) {
                            valueToUse =
                              typeof exampleValue === 'object'
                                ? JSON.stringify(exampleValue)
                                : exampleValue;
                            typeToUse =
                              typeof exampleValue === 'object'
                                ? 'object'
                                : typeof exampleValue;
                          } else if (propSchema.example !== undefined) {
                            valueToUse =
                              propSchema.type === 'object'
                                ? JSON.stringify(propSchema.example)
                                : propSchema.example;
                          }

                          typeToUse =
                            propSchema.format === 'binary' ? 'file' : typeToUse;

                          return {
                            key: propName,
                            format: propSchema.format || '',
                            value: valueToUse,
                            type: typeToUse,
                            description:
                              propSchema.description ||
                              `Form field: ${propName}`,
                            required:
                              requestData.schema.required?.includes(propName) ||
                              false,
                          };
                        });

                        setFormDataParams(formFields);
                      } else {
                        const formFields = Object.entries(
                          newExampleValue || {}
                        ).map(([key, value]) => {
                          return {
                            key,
                            format: '',
                            value:
                              typeof value === 'object'
                                ? JSON.stringify(value)
                                : String(value),
                            type:
                              typeof value === 'object'
                                ? 'object'
                                : typeof value,
                            description: `Form field: ${key}`,
                            required: false,
                          };
                        });

                        setFormDataParams(formFields);
                      }
                    } else {
                      setFormDataParams([]);
                      setSelectedExample(newExampleValue);
                    }
                  }
                }}
                width="250px"
                options={responseOptions}
                model="response"
              />
              <Button
                css={{ width: 150, borderRadius: 4 }}
                size="38px"
                fontSize="15px"
                variant="outline"
                onClick={() => {
                  const currentParams = getCurrentParameters();

                  openCodeModal(true);

                  setQueryParams(currentParams.queryParams);
                  setHeaderParams(currentParams.headerParams);
                  setPathParams(currentParams.pathParams);
                  setFormDataParams(currentParams.formDataParams);
                }}
              >
                Code Snippet
              </Button>
            </Layout.Group>
          </ActionContainer>
          <Tabs
            size="$sm"
            variant="line"
            grow={false}
            css={{
              'abyss-tabs-list': {
                border: 'none',
                minHeight: 40,
              },
              'abyss-tabs-content-container': {
                border: 'none',
                paddingLeft: 0,
                paddingRight: 0,
              },
            }}
          >
            {queryParams.length > 0 && (
              <Tabs.Tab label="Query">
                <ParameterTable
                  title="Query Parameters"
                  parameters={queryParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
            {headerParams.length > 0 && (
              <Tabs.Tab label="Headers">
                <ParameterTable
                  title="Headers"
                  parameters={headerParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
            {pathParams.length > 0 && (
              <Tabs.Tab label="Path">
                <ParameterTable
                  title="Path"
                  parameters={pathParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
            {selectedExample && (
              <Tabs.Tab label="Body">
                <BodyV2 body={selectedExample} currentApi={currentApi} />
              </Tabs.Tab>
            )}
            {formDataParams.length > 0 && (
              <Tabs.Tab label="Form Data">
                <ParameterTable
                  title="Form Data"
                  parameters={formDataParams}
                  setValue={form.setValue}
                />
              </Tabs.Tab>
            )}
          </Tabs>
          <ResponseHeading>Response:</ResponseHeading>
          <Response response={serviceResponse} />
        </FormProvider>
      </SandBoxContainer>
      <Modal
        size="800px"
        title="Code Snippet"
        isOpen={isCodeOpen}
        onClose={() => {
          return openCodeModal(false);
        }}
      >
        <CodeSnippet
          url={url}
          method={method}
          query={queryParams}
          headers={headerParams}
          body={selectedExample}
          formData={formDataParams}
          path={pathParams}
        />
      </Modal>
    </React.Fragment>
  );
};
